import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import EmptyState from '../components/EmptyState';
import { fetchSupplierOrders, transitionSupplierOrderStatus } from '../api/marketplaceApi';
import type { SupplierOrder } from '../types';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending_supplier_confirmation', label: 'Awaiting Confirmation' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'stock_reserved', label: 'Stock Reserved' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready_for_quality_check', label: 'Quality Check' },
  { value: 'quality_accepted', label: 'Quality Accepted' },
  { value: 'quality_rejected', label: 'Quality Rejected' },
  { value: 'correction_required', label: 'Correction Required' },
  { value: 'ready_for_dispatch', label: 'Ready for Dispatch' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'buyer_accepted', label: 'Buyer Accepted' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'disputed', label: 'Disputed' },
];

const SupplierOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [transitionOrderId, setTransitionOrderId] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: 100 };
      if (filter) params.status = filter;
      const res = await fetchSupplierOrders(params);
      setOrders((res.items || []) as SupplierOrder[]);
    } catch (err) {
      console.error('Failed to fetch supplier orders:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleStatusTransition = async (orderId: number, nextStatus: string, notes?: string) => {
    setTransitioning(true);
    try {
      await transitionSupplierOrderStatus(orderId, nextStatus, notes);
      setTransitionOrderId(null);
      await loadOrders();
    } catch (err: any) {
      alert(err.message || 'Failed to update status');
    } finally {
      setTransitioning(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const activeCount = orders.filter(
    (o) => !['completed', 'cancelled', 'disputed'].includes(o.status)
  ).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Supplier Orders</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {activeCount} active{orderCountLabel(activeCount)} · {orders.length} total
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-56 pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors appearance-none"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )
      // Empty
      : orders.length === 0 ? (
        <EmptyState
          icon={<DocumentDuplicateIcon className="w-12 h-12" />}
          title="No supplier orders"
          description={filter
            ? 'No orders match the selected status filter.'
            : 'Orders from buyers will appear here when they confirm purchases from your listings.'}
          action={!filter
            ? { label: 'Browse Catalogue', onClick: () => navigate('/marketplace/supplier/listings') }
            : undefined}
        />
      )
      // Table
      : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Order #
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Parent Order
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Received
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50/70 transition-colors group"
                  >
                    {/* Order Number */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                          {order.order_number}
                        </span>
                        {['pending_supplier_confirmation', 'confirmed', 'stock_reserved',
                          'preparing', 'ready_for_quality_check', 'quality_accepted',
                          'ready_for_dispatch', 'in_transit', 'delivered',
                          'buyer_accepted', 'correction_required', 'disputed'
                        ].includes(order.status) && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Active
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Parent Order */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-500">
                        {order.parent_order_number || '—'}
                      </span>
                      {order.parent_total != null && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Parent total: <span className="font-mono text-gray-500">{order.currency} {Number(order.parent_total).toLocaleString()}</span>
                        </p>
                      )}
                    </td>

                    {/* Amount */}
                    <td className="px-4 py-3">
                      <div className="flex items-baseline gap-2">
                        <Money
                          amount={order.subtotal ?? order.total_amount ?? 0}
                          currency={order.currency}
                          className="text-gray-900 font-semibold text-sm"
                        />
                        {order.commission_amount != null && order.commission_amount > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                            -{Number(order.commission_percentage ?? 0).toFixed(1)}%
                          </span>
                        )}
                      </div>
                      {order.total_amount != null &&
                       order.subtotal != null &&
                       order.total_amount !== order.subtotal && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          You receive: <span className="font-mono text-gray-500">{order.currency} {Number(order.total_amount).toLocaleString()}</span>
                        </p>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} size="sm" />
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{formatDate(order.created_at)}</span>
                      {order.confirmed_at && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Confirmed {formatDate(order.confirmed_at)}
                        </p>
                      )}
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/marketplace/supplier/orders/${order.id}`)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={transitioning}
                      >
                        View
                        <ArrowRightIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer summary */}
          {orders.length > 0 && (
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
              <span>{orders.length} order{orders.length !== 1 ? 's' : ''} shown</span>
              <span>Page 1 · showing first {orders.length}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function orderCountLabel(active: number): string {
  if (active === 0) return '';
  if (active === 1) return ' order';
  return ' orders';
}

export default SupplierOrders;
